import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Chip,
  Box,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  MenuItem,
} from '@mui/material';
import { GooglePlacesAutocomplete } from './GooglePlacesAutocomplete';
import {
  LocationOn as LocationIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useDataProvider, useNotify } from 'react-admin';
import { geocodeAddress } from '@/utils/geocoding';

interface Address {
  id?: string;
  name?: string;
  type: string;
  street: string; // Frontend field name
  streetLine2?: string; // Frontend field name for address line 2
  streetLine1?: string; // Server field name
  city: string;
  state: string; // Frontend field name
  province?: string; // Server field name
  zipCode: string; // Frontend field name
  postalCode?: string; // Server field name
  country: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  notes?: string;
  zoneId?: string;
  active?: boolean;
  latitude?: number;
  longitude?: number;
}

interface AddressTableProps {
  customerId: string;
}

export const AddressTable: React.FC<AddressTableProps> = ({ customerId }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [zones, setZones] = useState<any[]>([]);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [viewingAddress, setViewingAddress] = useState<Address | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState<Address>({
    name: '',
    type: 'service',
    street: '',
    streetLine2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'Canada',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    notes: '',
    zoneId: '',
    active: true,
  });

  const loadAddresses = useCallback(async () => {
    try {
      const response = await dataProvider.getList('customer-addresses', {
        filter: { customerId },
        sort: { field: 'type', order: 'ASC' },
        pagination: { page: 1, perPage: 50 },
      });
      setAddresses(response.data);
    } catch (error) {
      notify('Error loading addresses', { type: 'error' });
    }
  }, [dataProvider, customerId, notify]);

  const loadZones = useCallback(async () => {
    try {
      const response = await dataProvider.getList('crm/zones', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' },
        filter: {},
      });
      setZones(response.data);
    } catch (error) {
      console.error('Error loading zones:', error);
      setZones([]);
    }
  }, [dataProvider]);

  useEffect(() => {
    loadAddresses();
    loadZones();
  }, [customerId, loadAddresses, loadZones]);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleAddressSelect = (address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    latitude?: number;
    longitude?: number;
  }) => {
    setFormData(prev => ({
      ...prev,
      name: prev.name || address.street, // Set name to street address if not already filled in
      street: address.street,
      city: address.city,
      state: address.state,
      zipCode: address.zipCode,
      country: address.country,
      latitude: address.latitude,
      longitude: address.longitude,
    }));
  };

  const handleView = (address: Address) => {
    setViewingAddress(address);
    setShowDialog(true);
  };

  const handleEdit = (address: Address) => {
    // Transform server data back to frontend format
    const frontendData = {
      ...address,
      street: address.streetLine1 || address.street || '',
      streetLine2: address.streetLine2 || '',
      state: address.province || address.state || '',
      zipCode: address.postalCode || address.zipCode || '',
      type: address.type?.toLowerCase() || 'service',
    };
    setEditingAddress(address);
    setFormData(frontendData);
    setShowDialog(false); // Close show dialog if open
    setDialogOpen(true);
  };

  // Helper function to transform data for server
  const transformAddressForServer = (address: Address) => {
    // Capitalize the type to match server enum
    const capitalizeType = (type: string) => {
      return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
    };

    return {
      name: address.name || `${capitalizeType(address.type)} Address`,
      streetLine1: address.street,
      streetLine2: address.streetLine2 || undefined,
      city: address.city,
      province: address.state,
      postalCode: address.zipCode,
      country: address.country,
      type: capitalizeType(address.type),
      customerId,
      active: address.active,
      zoneId: address.zoneId || undefined,
      contactName: address.contactName || undefined,
      contactPhone: address.contactPhone || undefined,
      contactEmail: address.contactEmail || undefined,
      notes: address.notes || undefined,
      latitude: address.latitude,
      longitude: address.longitude,
    };
  };

  const handleSave = async () => {
    try {
      // Geocode address if latitude/longitude are not already set
      let addressData = { ...formData };
      
      if (!addressData.latitude || !addressData.longitude) {
        const geocodeResult = await geocodeAddress({
          streetLine1: addressData.street,
          streetLine2: addressData.streetLine2,
          city: addressData.city,
          state: addressData.state,
          zipCode: addressData.zipCode,
          country: addressData.country,
        });

        if (geocodeResult) {
          addressData.latitude = geocodeResult.latitude;
          addressData.longitude = geocodeResult.longitude;
        }
      }

      const serverData = transformAddressForServer(addressData);

      if (editingAddress) {
        await dataProvider.update('customer-addresses', {
          id: editingAddress.id,
          data: serverData,
          previousData: editingAddress,
        });
        notify('Address updated successfully', { type: 'success' });
      } else {
        await dataProvider.create('customer-addresses', {
          data: serverData,
        });
        notify('Address created successfully', { type: 'success' });
      }
      setDialogOpen(false);
      setEditingAddress(null);
      setFormData({
        name: '',
        type: 'service',
        street: '',
        streetLine2: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'Canada',
        contactName: '',
        contactPhone: '',
        contactEmail: '',
        notes: '',
        zoneId: '',
        active: true,
      });
      loadAddresses();
    } catch (error) {
      notify('Error saving address', { type: 'error' });
    }
  };

  const handleAddNew = () => {
    setEditingAddress(null);
    setFormData({
      name: '',
      type: 'service',
      street: '',
      streetLine2: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'Canada',
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      notes: '',
      zoneId: '',
      active: true,
    });
    setDialogOpen(true);
  };

  return (
    <>
      <Card>
        <CardContent>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationIcon color="primary" />
              Addresses
            </Typography>
            <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={handleAddNew}>
              Add Address
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {addresses.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
              <LocationIcon sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="body1">
                No addresses found. Click &quot;Add Address&quot; to create one.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell>State</TableCell>
                    <TableCell>ZIP</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {addresses.map(address => (
                    <TableRow
                      key={address.id}
                      hover
                      onClick={() => handleView(address)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{address.name || `${address.type} Address`}</TableCell>
                      <TableCell>
                        <Chip
                          label={address.type}
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        {address.streetLine1 || address.street}
                        {address.streetLine2 && (
                          <>
                            <br />
                            <span style={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                              {address.streetLine2}
                            </span>
                          </>
                        )}
                      </TableCell>
                      <TableCell>{address.city}</TableCell>
                      <TableCell>{address.province || address.state}</TableCell>
                      <TableCell>{address.postalCode || address.zipCode}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Show/View Dialog */}
      <Dialog open={showDialog} onClose={() => setShowDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Address Details</Typography>
            <IconButton onClick={() => setShowDialog(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {viewingAddress && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="h6" gutterBottom>
                {viewingAddress.name || `${viewingAddress.type} Address`}
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Address
                </Typography>
                <Typography variant="body1">
                  {viewingAddress.streetLine1 || viewingAddress.street}
                  {viewingAddress.streetLine2 && (
                    <>
                      <br />
                      {viewingAddress.streetLine2}
                    </>
                  )}
                </Typography>
                <Typography variant="body1">
                  {viewingAddress.city}, {viewingAddress.province || viewingAddress.state} {viewingAddress.postalCode || viewingAddress.zipCode}
                </Typography>
                <Typography variant="body1">{viewingAddress.country}</Typography>
              </Box>

              {(viewingAddress.contactName || viewingAddress.contactPhone || viewingAddress.contactEmail) && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Contact Information
                  </Typography>
                  {viewingAddress.contactName && (
                    <Typography variant="body1">Name: {viewingAddress.contactName}</Typography>
                  )}
                  {viewingAddress.contactPhone && (
                    <Typography variant="body1">Phone: {viewingAddress.contactPhone}</Typography>
                  )}
                  {viewingAddress.contactEmail && (
                    <Typography variant="body1">Email: {viewingAddress.contactEmail}</Typography>
                  )}
                </Box>
              )}

              {viewingAddress.zoneId && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Service Zone
                  </Typography>
                  <Typography variant="body1">
                    {zones.find(zone => zone.id === viewingAddress.zoneId)?.name || viewingAddress.zoneId}
                  </Typography>
                </Box>
              )}

              {viewingAddress.notes && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Notes
                  </Typography>
                  <Typography variant="body1">{viewingAddress.notes}</Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={viewingAddress.type}
                  size="small"
                  sx={{ textTransform: 'capitalize' }}
                />
                {viewingAddress.active && (
                  <Chip label="Active" size="small" color="success" />
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDialog(false)}>Close</Button>
          {viewingAddress && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => handleEdit(viewingAddress)}
            >
              Edit Address
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              {editingAddress ? 'Edit Address' : 'Add New Address'}
            </Typography>
            <IconButton onClick={() => setDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Address Name"
                value={formData.name}
                onChange={handleChange('name')}
                placeholder="e.g., Main Office, Warehouse, etc."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Address Type"
                value={formData.type}
                onChange={handleChange('type')}
                select
                SelectProps={{
                  native: true,
                }}
              >
                <option value="service">Service</option>
                <option value="billing">Billing</option>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <GooglePlacesAutocomplete
                value={formData.street}
                onChange={value => setFormData(prev => ({ ...prev, street: value }))}
                onAddressSelect={handleAddressSelect}
                label="Street Address"
                placeholder="Start typing an address..."
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2"
                value={formData.streetLine2}
                onChange={handleChange('streetLine2')}
                placeholder="Apartment, suite, unit, building, floor, etc."
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={handleChange('city')}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="State"
                value={formData.state}
                onChange={handleChange('state')}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="ZIP Code"
                value={formData.zipCode}
                onChange={handleChange('zipCode')}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Country"
                value={formData.country}
                onChange={handleChange('country')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Service Zone"
                value={formData.zoneId || ''}
                onChange={handleChange('zoneId')}
                select
                SelectProps={{
                  native: false,
                }}
              >
                <MenuItem value="">
                  <em>Select a zone</em>
                </MenuItem>
                {zones.map((zone) => (
                  <MenuItem key={zone.id} value={zone.id}>
                    {zone.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                Contact Information
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Name"
                value={formData.contactName || ''}
                onChange={handleChange('contactName')}
                placeholder="Primary contact for this address"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Phone"
                value={formData.contactPhone || ''}
                onChange={handleChange('contactPhone')}
                placeholder="Contact phone number"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Contact Email"
                type="email"
                value={formData.contactEmail || ''}
                onChange={handleChange('contactEmail')}
                placeholder="Contact email address"
              />
            </Grid>

            {/* Additional Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                Additional Information
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes || ''}
                onChange={handleChange('notes')}
                placeholder="Additional notes about this address"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingAddress ? 'Update Address' : 'Add Address'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
